import React, { ReactElement, useEffect, useState } from 'react'
import {
  apiMarkNotificationsAsRead,
  NotificationData,
  NotificationDataType
} from 'src/api'
import { apiGetNotifications } from '../../../api/services/isloggedinwebsocket/isloggedinwebsocket'
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks'
import { selectUser } from '../../../redux/slices/UserSlice/UserSlice.selectors'
import NotificationAlertIcon from 'src/assets/icons/notification_alert.svg'
import NotificationAirbnbIcon from 'src/assets/icons/notification_airbnb.svg'
import NotificationReserveIcon from 'src/assets/icons/notification_reserve.svg'
import NotificationMessageIcon from 'src/assets/icons/notification_message.svg'
import { useTranslation } from 'react-i18next'
import useSentry from 'src/hooks/useSentry'
import { removeUser } from 'src/redux'

export interface UseUserNotificationsReturnType {
  notifications: NotificationData[]
  notificationOpenInstructions: {
    [key in NotificationDataType]: (notification: NotificationData) => void
  }
  notificationTypeLabels: { [key in NotificationDataType]: string }
  notificationIcons: { [key in NotificationDataType]: ReactElement }
  closeNotification: (notification: NotificationData) => void
  closeAllNotifications: () => void
}

const useUserNotifications = (): UseUserNotificationsReturnType => {
  const dispatch = useAppDispatch()
  const { serverUrl: serverPath, token: userToken } = useAppSelector(selectUser)
  const { t } = useTranslation()

  const { captureError } = useSentry()
  const [notifications, setNotifications] = useState<NotificationData[]>([])

  let fetchNotificationsInterval: NodeJS.Timer

  const getNotifications = async () => {
    try {
      const res = await apiGetNotifications({
        serverPath,
        userToken
      })
      const { notificationDataList, logged } = res
      // if (!logged) return dispatch(removeUser())
      setNotifications(
        notificationDataList.slice().sort((a, b) => {
          // type alerts first
          if (
            a.type !== NotificationDataType.Alertas &&
            b.type === NotificationDataType.Alertas
          ) {
            return 1
          }
          return 0
        })
      )
    } catch (error) {
      console.log(error)
      captureError(error, {
        description: 'error from getNotifications useUserNotifications'
      })
    }
  }

  useEffect(() => {
    ;(async () => {
      await getNotifications()
      fetchNotificationsInterval = setInterval(async () => {
        await getNotifications()
      }, 1000 * 60 * 5)
    })()
    return () => {
      clearInterval(fetchNotificationsInterval)
    }
  }, [])

  const openReserve = (reserveId: string) => {
    window.openAppFromOldFront(
      'reserveWindowId',
      `https://${serverPath}/sialdPMS/secure/logged/dialogs/reservation/layout/reservationDialogLayout.xhtml?reserveid=${reserveId}`
    )
  }

  const callApiMarkNotificationsAsRead = async (
    notificationIdList: string[]
  ) => {
    try {
      if (notificationIdList.length < 1) return
      await apiMarkNotificationsAsRead({
        serverPath,
        data: {
          notificationList: notificationIdList,
          userToken
        }
      })
    } catch (error) {
      console.log(error)
      captureError(error, {
        description:
          'error from callApiMarkNotificationsAsRead useUserNotifications.tsx'
      })
    }
  }

  const closeAllNotifications = () => {
    const notificationIdList = notifications.map(
      (notification) => notification.id
    )
    setNotifications([])
    callApiMarkNotificationsAsRead(notificationIdList)
  }

  const closeNotification = (notification: NotificationData) => {
    setNotifications((notifications) =>
      notifications.filter((n) => n.id !== notification.id)
    )
    // callApiMarkNotificationsAsRead([notification.id])
  }

  const notificationOpenInstructions: {
    [key in NotificationDataType]: (notification: NotificationData) => void
  } = {
    peticiones_airbnb: () => {
      window.openAppFromOldFront(
        '59B6C7CD8B3F454E880323B6DCC44B9C',
        `https://${serverPath}/sialdPMS/secure/logged/dialogs/dashboard/layout/dashboardDialogLayout.xhtml`
      )
    },
    mensajes_clientes: ({ entityId }) => openReserve(entityId!),
    alertas: () => {
      window.openAppFromOldFront('6BC39B80DE99479C8B6F9023723E05EF')
    },
    [NotificationDataType.ReservasSinAsignar]: ({ entityId }) =>
      openReserve(entityId!),
    [NotificationDataType.ReservasNoLeidas]: ({ entityId }) =>
      openReserve(entityId!),
    [NotificationDataType.ReservasEnEspera]: ({ entityId }) =>
      openReserve(entityId!)
  }

  const notificationTypeLabels: {
    [key in NotificationDataType]: string
  } = {
    alertas: t('labels.Alerts'),
    mensajes_clientes: t('labels.Reservation_messages'),
    peticiones_airbnb: t('labels.Airbnb_requests'),
    [NotificationDataType.ReservasSinAsignar]: t(
      'labels.Unassigned_reservations'
    ),
    [NotificationDataType.ReservasNoLeidas]: t('labels.Unread_reservations'),
    [NotificationDataType.ReservasEnEspera]: t('labels.Awaiting_reservations')
  }

  const notificationIcons: {
    [key in NotificationDataType]: ReactElement
  } = {
    alertas: <img src={NotificationAlertIcon} alt='alert' />,
    mensajes_clientes: <img src={NotificationMessageIcon} alt='alert' />,
    peticiones_airbnb: <img src={NotificationAirbnbIcon} alt='alert' />,
    [NotificationDataType.ReservasSinAsignar]: (
      <img src={NotificationReserveIcon} alt='alert' />
    ),
    [NotificationDataType.ReservasEnEspera]: (
      <img src={NotificationReserveIcon} alt='alert' />
    ),
    [NotificationDataType.ReservasNoLeidas]: (
      <img src={NotificationReserveIcon} alt='alert' />
    )
  }

  return {
    notifications,
    notificationOpenInstructions,
    notificationTypeLabels,
    notificationIcons,
    closeNotification,
    closeAllNotifications
  }
}
export default useUserNotifications
